import React from 'react';

const C = {
  surface: 'var(--lyra-color-surface)',
  surface2: 'var(--lyra-color-surface-2)',
  border: 'var(--lyra-color-border)',
  borderStrong: 'var(--app-border-strong)',
  ink: 'var(--lyra-color-text)',
  ink2: 'var(--app-text-2)',
  ink3: 'var(--lyra-color-text-muted)',
  ink4: 'var(--app-text-3)',
  accent: 'var(--lyra-color-primary)',
  accentSoft: 'var(--app-primary-soft)',
  accentSoft2: 'var(--app-primary-soft-2)',
  success: 'var(--lyra-color-success)',
};

export function EntityCardShell({ children, onClick, className = '' }) {
  return (
    <article
      className={`lyra-entity-card ${className}`}
      onClick={onClick}
      style={{
        position: 'relative', minHeight: 350, overflow: 'visible',
        display: 'flex', flexDirection: 'column', padding: 20,
        background: C.surface, border: `1px solid ${C.border}`,
        borderTop: `3px solid ${C.accent}`, borderRadius: 16,
        cursor: onClick ? 'pointer' : 'default',
        boxShadow: '0 1px 2px color-mix(in srgb, var(--lyra-color-text) 5%, transparent)',
        transition: 'transform .16s ease, box-shadow .16s ease, border-color .16s ease',
      }}
      onMouseEnter={e => {
        e.currentTarget.style.transform = 'translateY(-2px)';
        e.currentTarget.style.boxShadow = '0 10px 28px color-mix(in srgb, var(--lyra-color-text) 9%, transparent)';
        e.currentTarget.style.borderColor = C.borderStrong;
      }}
      onMouseLeave={e => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = '0 1px 2px color-mix(in srgb, var(--lyra-color-text) 5%, transparent)';
        e.currentTarget.style.borderColor = C.border;
      }}
    >
      {children}
    </article>
  );
}

export function EntityCardHeader({ icon: Icon, eyebrow, title, status, statusTone = 'success', menu }) {
  const statusColor = statusTone === 'success' ? C.success : C.accent;
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, minHeight: 74 }}>
      <div style={{
        width: 48, height: 48, borderRadius: 12, flexShrink: 0,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        color: C.accent, background: C.accentSoft, border: `1px solid ${C.accentSoft2}`,
      }}>
        <Icon size={23} strokeWidth={1.8} />
      </div>
      <div style={{ flex: 1, minWidth: 0, paddingTop: 1 }}>
        <div style={{ fontSize: 10.5, lineHeight: 1.2, fontWeight: 700, letterSpacing: '.085em', color: C.ink3, textTransform: 'uppercase', marginBottom: 5 }}>
          {eyebrow}
        </div>
        <div title={title} style={{ fontSize: 18, lineHeight: 1.2, fontWeight: 650, color: C.ink, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {title}
        </div>
        {status && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginTop: 6, color: statusColor, fontSize: 11.5, fontWeight: 600 }}>
            <span style={{ width: 7, height: 7, borderRadius: '50%', background: statusColor, boxShadow: `0 0 0 3px color-mix(in srgb, ${statusColor} 12%, transparent)` }} />
            {status}
          </div>
        )}
      </div>
      {menu && <div onClick={e => e.stopPropagation()} style={{ flexShrink: 0, marginTop: -2 }}>{menu}</div>}
    </div>
  );
}

export function EntityCardDescription({ children }) {
  return (
    <div style={{
      minHeight: 48, marginTop: 16, color: C.ink3, fontSize: 13, lineHeight: 1.55,
      overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
    }}>
      {children}
    </div>
  );
}

export function EntityCardMeta({ icon: Icon, children }) {
  return (
    <div style={{
      minHeight: 42, marginTop: 12, padding: '11px 0',
      display: 'flex', alignItems: 'center', gap: 9,
      color: C.ink3, fontSize: 11.5, fontWeight: 500,
      borderTop: `1px solid ${C.border}`, borderBottom: `1px solid ${C.border}`,
    }}>
      {Icon && <Icon size={15} strokeWidth={1.8} color={C.ink3} />}
      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{children}</span>
    </div>
  );
}

export function EntityCardMetrics({ items }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: `repeat(${Math.max(items.length, 1)}, minmax(0, 1fr))`, padding: '15px 0 14px' }}>
      {items.map((item, index) => (
        <div key={`${item.label}-${index}`} style={{ minWidth: 0, paddingLeft: index ? 14 : 0, borderLeft: index ? `1px solid ${C.border}` : 'none' }}>
          <div style={{ color: item.tone === 'success' ? C.success : C.ink, fontSize: 16, lineHeight: 1.2, fontWeight: 650, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {item.value}
          </div>
          <div style={{ marginTop: 4, color: C.ink4, fontSize: 10.5, lineHeight: 1.2, whiteSpace: 'nowrap' }}>{item.label}</div>
        </div>
      ))}
    </div>
  );
}

export function EntityCardFooter({ children }) {
  return <div style={{ display: 'flex', gap: 8, marginTop: 'auto', paddingTop: 2 }}>{children}</div>;
}

export function EntityPrimaryButton({ children, onClick, title, disabled = false }) {
  return (
    <button
      onClick={onClick}
      title={title}
      disabled={disabled}
      style={{
        height: 40, minWidth: 0, flex: 1, padding: '0 16px', border: 'none', borderRadius: 10,
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8,
        background: C.accent, color: 'var(--app-on-accent)', cursor: disabled ? 'not-allowed' : 'pointer',
        fontFamily: 'inherit', fontSize: 12.5, fontWeight: 650,
        opacity: disabled ? .55 : 1,
      }}
      onMouseEnter={e => { if (!disabled) e.currentTarget.style.filter = 'brightness(.96)'; }}
      onMouseLeave={e => { e.currentTarget.style.filter = 'none'; }}
    >
      {children}
    </button>
  );
}

export function EntityIconButton({ children, onClick, title }) {
  return (
    <button
      onClick={onClick}
      title={title}
      aria-label={title}
      style={{
        width: 40, height: 40, flexShrink: 0, borderRadius: 10,
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        border: `1px solid ${C.border}`, background: C.surface2, color: C.ink2, cursor: 'pointer',
      }}
      onMouseEnter={e => { e.currentTarget.style.background = 'var(--app-surface-3)'; }}
      onMouseLeave={e => { e.currentTarget.style.background = C.surface2; }}
    >
      {children}
    </button>
  );
}

export const entityCardColors = C;
